import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useSupabaseQuery } from '../../hooks/useSupabase';
import { UserRole } from '../../types/database';
import { useAdminLayoutContext } from '../../components/admin/AdminLayout';
import {
  Plus,
  Edit2,
  Trash2,
  Mail,
  Shield,
  User,
  X,
  Building2,
  ArrowLeft,
} from 'lucide-react';

interface UserProfile {
  id: string;
  email: string;
  full_name?: string;
  phone?: string;
  role: UserRole;
  company_id?: string;
  department_id?: string;
  is_active?: boolean;
}

export function UserManagement() {
  const { profile, isSuperAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setHeaderAction, setPageTitle, setPageSubtitle, setBackTo } = useAdminLayoutContext();
  const departmentIdParam = searchParams.get('department_id');
  const departmentNameParam = searchParams.get('department_name');

  // Get department info if coming from department page
  const { data: department } = useSupabaseQuery<any>(
    'departments',
    {
      filter: (q: any) => q.eq('id', departmentIdParam),
      single: true,
      enabled: !!departmentIdParam,
    }
  );

  // Get company info if we have a department
  const { data: company } = useSupabaseQuery<any>(
    'companies',
    {
      filter: (q: any) => q.eq('id', department?.company_id),
      single: true,
      enabled: !!department?.company_id,
    }
  );

  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Initialize formData with department info if available
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    phone: '',
    role: 'counselor' as UserRole,
    companyId: department?.company_id || profile?.companyId || '',
    departmentId: departmentIdParam || '',
  });

  // Build users query
  const usersFilter = React.useCallback((q: any) => {
    if (departmentIdParam) {
      return q.eq('department_id', departmentIdParam);
    } else if (profile?.companyId) {
      return q.eq('company_id', profile.companyId);
    }
    return q;
  }, [departmentIdParam, profile?.companyId]);

  const { data: users, loading: usersLoading, refetch: refetchUsers } = useSupabaseQuery<UserProfile[]>(
    'profiles',
    {
      select: '*',
      filter: usersFilter,
      order: { column: 'full_name', ascending: true },
    }
  );

  // Set page title, back button, and header action
  useEffect(() => {
    // Set dynamic page title based on context
    if (departmentIdParam && company && department) {
      setPageTitle(`${company.name} - ${department.name} - Brukere`);

      // Set back button to go to departments
      setBackTo(`/admin/departments?company_id=${department.company_id}&company_name=${encodeURIComponent(company.name)}`);
    } else {
      setPageTitle('Brukeradministrasjon');
      setBackTo(null);
    }

    // Set header action button
    setHeaderAction(
      <button
        onClick={() => setShowModal(true)}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
      >
        <Plus className="w-5 h-5" />
        Ny bruker
      </button>
    );

    return () => {
      setPageTitle(null);
      setHeaderAction(null);
      setBackTo(null);
    };
  }, [setHeaderAction, setPageTitle, setBackTo, departmentIdParam, company, department, navigate]);

  // Fetch companies for the form dropdown
  const { data: companies } = useSupabaseQuery<any[]>(
    'companies',
    {
      select: '*',
      order: { column: 'name', ascending: true },
    }
  );

  // Filter departments based on selected company in form or department's company
  const selectedCompanyId = formData.companyId || department?.company_id;
  const { data: departments } = useSupabaseQuery<any[]>(
    'departments',
    {
      select: '*',
      filter: (q: any) => q.eq('company_id', selectedCompanyId),
      order: { column: 'name', ascending: true },
      enabled: !!selectedCompanyId,
    }
  );

  // Update formData when department is loaded
  React.useEffect(() => {
    if (department && departmentIdParam) {
      setFormData(prev => ({
        ...prev,
        companyId: department.company_id,
        departmentId: departmentIdParam,
      }));
    }
  }, [department, departmentIdParam]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingUser) {
        // Update existing user
        const { error } = await supabase
          .from('profiles')
          .update({
            full_name: formData.fullName,
            phone: formData.phone || null,
            role: formData.role,
            company_id: formData.companyId || null,
            department_id: formData.departmentId || null,
            is_active: true,
          })
          .eq('id', editingUser.id);

        if (error) throw error;
      } else {
        // Create new user via RPC
        const { data, error } = await supabase.rpc('create_user_with_profile', {
          p_user_id: crypto.randomUUID(),
          p_email: formData.email,
          p_password: formData.password,
          p_full_name: formData.fullName,
          p_phone: formData.phone || null,
          p_role: formData.role,
          p_department_id: formData.departmentId || null,
          p_company_id: formData.companyId || null,
          p_is_active: true,
        });

        if (error) throw error;
      }

      setShowModal(false);
      setEditingUser(null);
      resetForm();
      refetchUsers();
    } catch (error: any) {
      console.error('Error saving user:', error);
      let errorMessage = error.message || error.toString();

      if (errorMessage.includes('User with this email already exists') || errorMessage.includes('duplicate key')) {
        errorMessage = 'En bruker med denne e-postadressen eksisterer allerede. Bruk en annen e-postadresse.';
      } else if (errorMessage.includes('No available seats')) {
        errorMessage = 'Ingen ledige plasser i denne avdelingen.';
      }

      alert(`Feil ved lagring: ${errorMessage}`);
    }
  };

  const handleEdit = (user: UserProfile) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      fullName: user.full_name || '',
      phone: user.phone || '',
      role: user.role,
      companyId: user.company_id || '',
      departmentId: user.department_id || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (user: UserProfile) => {
    if (!window.confirm(`Er du sikker på at du vil slette ${user.full_name || user.email}?`)) {
      return;
    }

    try {
      const { error } = await supabase.from('profiles').delete().eq('id', user.id);
      if (error) throw error;
      refetchUsers();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      alert(`Feil ved sletting: ${error.message}`);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      fullName: '',
      phone: '',
      role: 'counselor' as UserRole,
      companyId: department?.company_id || profile?.companyId || '',
      departmentId: departmentIdParam || '',
    });
  };

  // Reset department when company changes (but not if coming from department page)
  React.useEffect(() => {
    if (formData.companyId && !departmentIdParam) {
      setFormData(prev => ({ ...prev, departmentId: '' }));
    }
  }, [formData.companyId, departmentIdParam]);

  const handleCancel = () => {
    setShowModal(false);
    setEditingUser(null);
    resetForm();
  };

  const getRoleName = (role: UserRole | string) => {
    const roleNames: Record<string, string> = {
      user: 'Bruker',
      administrator: 'Administrator',
      // Legacy roles for backwards compatibility
      superadmin: 'Administrator',
      org_admin: 'Administrator',
      editor: 'Bruker',
      counselor: 'Bruker',
      researcher: 'Bruker',
    };
    return roleNames[role] || role;
  };

  const userList = users || [];

  // Filter users by search term
  const filteredUsers = userList.filter((user) => {
    const matchesSearch =
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  if (!isSuperAdmin && profile?.role !== 'org_admin') {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-400">Du har ikke tilgang til denne siden.</p>
        </div>
      </div>
    );
  }

  const companiesList = companies || [];
  const departmentsList = departments || [];

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="flex gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Sok etter brukere..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Users List */}
      {usersLoading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Laster brukere...</p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <User className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            {searchTerm ? 'Ingen brukere funnet' : 'Ingen brukere registrert'}
          </p>
          <p className="text-gray-500 text-sm">
            {searchTerm ? 'Prov et annet sok' : 'Klikk på "Ny bruker" for å legge til den forste brukeren'}
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Bruker
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Rolle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Handlinger
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {user.full_name || 'Ingen navn'}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                      <span className="text-sm text-gray-900 dark:text-white">{getRoleName(user.role)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.is_active !== false
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800 dark:text-red-400'
                      }`}
                    >
                      {user.is_active !== false ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => handleEdit(user)}
                        className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                        title="Rediger"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {user.role !== 'superadmin' && (
                        <button
                          onClick={() => handleDelete(user)}
                          className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                          title="Slett"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && createPortal(
        <div className="fixed top-0 left-0 w-screen h-screen bg-black/50 dark:bg-black/70 flex items-center justify-center z-[9999]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingUser
                  ? 'Rediger bruker'
                  : departmentIdParam && departmentNameParam
                    ? `Ny bruker til ${departmentNameParam}`
                    : 'Ny bruker'}
              </h2>
              <button
                onClick={handleCancel}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Show department info at the top if coming from department */}
              {departmentIdParam && department && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <span className="font-semibold text-gray-900 dark:text-white">Avdeling</span>
                  </div>
                  <div className="text-lg font-medium text-gray-900 dark:text-white">
                    {departmentNameParam || department.name}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    <span className="font-medium">Firma: </span>
                    {companiesList.find(c => c.id === department.company_id)?.name || 'Laster...'}
                  </div>
                </div>
              )}
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  E-post *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                  required
                  disabled={!!editingUser}
                />
              </div>

              {/* Password (only for new users) */}
              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Passord *
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    minLength={6}
                  />
                </div>
              )}

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fullt navn *
                </label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Telefon
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Company (only for superadmin when creating new user, and NOT when coming from department) */}
              {!editingUser && isSuperAdmin && companiesList.length > 0 && !departmentIdParam && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Firma *
                  </label>
                  <select
                    value={formData.companyId}
                    onChange={(e) => setFormData({ ...formData, companyId: e.target.value, departmentId: '' })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Velg firma</option>
                    {companiesList.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Department dropdown (only if NOT coming from department page) */}
              {departmentsList.length > 0 && (formData.companyId || editingUser) && !departmentIdParam && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Avdeling {!editingUser && formData.companyId ? '*' : ''}
                  </label>
                  <select
                    value={formData.departmentId}
                    onChange={(e) => setFormData({ ...formData, departmentId: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required={!editingUser && !!formData.companyId}
                  >
                    <option value="">Velg avdeling</option>
                    {departmentsList.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name} {dept.allocated_seats !== undefined ? `(${dept.allocated_seats} plasser)` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rolle *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="counselor">Bruker</option>
                  <option value="org_admin">Lokal Admin</option>
                </select>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                >
                  {editingUser ? 'Oppdater' : 'Opprett'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
